import React, { useState, useRef, useEffect, useMemo } from "react";
import { Autocomplete, Chip, CircularProgress, TextField, Box } from "@mui/material";
import { ProductV2Modal } from "../../admin/productsV2/productModal";
import axiosInstant from "../../../api";
import { selectorAutocompleteStyle } from "./selectorStyles";

/** A product (a.k.a. "desire") as returned by `GET /brands/:id/desires`. */
export interface Product {
    id?: number | string;
    desireid?: number | string;
    title: string;
    image?: string;
    url?: string;
    brands?: unknown[];
    /**
     * Set by /rmn/products?include_discovered=1 (TDC-1499): "desire" rows are
     * imported catalog products keyed by desireid, "discovered" rows are crawl
     * staging rows keyed by id (selecting one in the strategy wizard
     * auto-imports it server-side).
     */
    kind?: "desire" | "discovered";
}

/**
 * Synthetic option injected into the dropdown so the user can create a brand-new
 * product inline when their search matches nothing.
 */
interface AddNewOption {
    addNew: true;
    /** Display label shown in the dropdown, e.g. `Add "Widget"`. */
    title: string;
    /** The raw text the user typed, used to pre-fill the create-product modal. */
    inputValue: string;
}

type ProductOption = Product | AddNewOption;

const isAddNewOption = (option: ProductOption): option is AddNewOption => (option as AddNewOption).addNew === true;

/** True when `brandId` points at a real, persisted brand whose catalog we can fetch. */
const isFetchableBrandId = (brandId: number | string | null | undefined): brandId is number | string =>
    brandId != null && brandId !== "new" && !Number.isNaN(Number(brandId));

/**
 * Stable identity for a product across the `id`/`desireid` shapes the API
 * returns. Discovered (staging) rows get a `dp-` prefix so their ids can never
 * collide with a desireid.
 */
const productKey = (product: Product): string | number =>
    product.kind === "discovered" ? `dp-${product.id}` : product.desireid ?? product.id ?? product.title;

/** Option/chip label — flags staging rows that aren't in the catalog yet. */
const displayTitle = (product: Product): string =>
    product.kind === "discovered" && product.title ? `${product.title} · not imported` : product.title;

export interface InlineProductSelectorProps {
    /** Formik `setFieldValue`. Kept in sync for `product_name`/`product_description`/`products`. */
    setFieldValue: (field: string, value: any) => void;
    /** Selected product in single-select mode. */
    selectedProduct: Product | null;
    setSelectedProduct: (product: Product | null) => void;
    /** Brand whose catalog is searched. `null`/`"new"`/non-numeric disables fetching. */
    selectedBrandId: number | string | null | undefined;
    /** Brand name, forwarded to the inline "create product" modal. */
    selectedBrandName?: string;
    disabled?: boolean;
    /** When true, render a multi-select with chips backed by `selectedProducts`. */
    multiple?: boolean;
    selectedProducts?: Product[];
    onProductsChange?: (products: Product[]) => void;
    /**
     * When provided, products are fetched from this endpoint with
     * `?brand_id=<selectedBrandId>&search=` instead of `/brands/:id/products`
     * — used by the RMN portal to scope the catalog to /rmn/products.
     */
    productsEndpoint?: string;
    /** When false, the `Add "<text>"` create-product option is suppressed. */
    allowAdd?: boolean;
    /**
     * When false, no brand selection is needed: the brand gating is skipped
     * and no `brand_id` param is sent — the endpoint scopes server-side
     * (the RMN strategy wizard's network-wide catalog search).
     */
    requireBrand?: boolean;
    /** Extra query params merged into the product fetch (e.g. include_discovered). */
    extraFetchParams?: Record<string, string | number>;
}

export const InlineProductSelector: React.FC<InlineProductSelectorProps> = ({
    setFieldValue,
    selectedProduct,
    setSelectedProduct,
    selectedBrandId,
    selectedBrandName,
    disabled = false,
    multiple = false,
    selectedProducts = [],
    onProductsChange,
    productsEndpoint,
    allowAdd = true,
    requireBrand = true,
    extraFetchParams
}) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchInput = useRef("");
    // Monotonic id used to discard responses from superseded (out-of-order) requests.
    const requestIdRef = useRef(0);

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<Product[]>([]);
    const [inputText, setInputText] = useState("");
    const [hasValidProducts, setHasValidProducts] = useState<boolean | null>(null);
    const [showProductModal, setShowProductModal] = useState(false);
    const [pendingProductName, setPendingProductName] = useState("");

    useEffect(() => {
        if (!multiple && !selectedProduct) setInputText("");
    }, [selectedProduct, multiple]);

    // Clear any pending debounced fetch when the component unmounts.
    useEffect(
        () => () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        },
        []
    );

    const fetchProducts = async () => {
        if (requireBrand && !isFetchableBrandId(selectedBrandId)) {
            setOptions([]);
            return;
        }

        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const { data } = await axiosInstant.get<{ rows?: Product[] }>(
                productsEndpoint || `/brands/${selectedBrandId}/products`,
                {
                    params: {
                        search: searchInput.current,
                        limit: 100,
                        ...(productsEndpoint && requireBrand ? { brand_id: selectedBrandId } : {}),
                        ...(extraFetchParams || {})
                    }
                }
            );
            // A newer request started since this one; ignore the stale response.
            if (requestId !== requestIdRef.current) return;
            const rows = data.rows ?? [];
            setOptions(rows);
            if (!searchInput.current.trim()) setHasValidProducts(rows.length > 0);
        } catch {
            if (requestId === requestIdRef.current) setOptions([]);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    };

    // Reset any in-flight search when the brand changes so the new brand's catalog
    // isn't filtered by the previous brand's search term. Defined before the fetch
    // effect so the ref is cleared before fetchProducts reads it on a brand switch.
    useEffect(() => {
        searchInput.current = "";
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setInputText("");
    }, [selectedBrandId]);

    useEffect(() => {
        if (open && (!requireBrand || isFetchableBrandId(selectedBrandId))) {
            setHasValidProducts(null);
            fetchProducts();
        } else {
            setOptions([]);
        }
        // Refetch when the dropdown opens or the brand changes; fetchProducts
        // intentionally reads the latest brand id and search ref each call.
    }, [open, selectedBrandId]);

    const handleSelectProduct = (product: Product | null) => {
        if (multiple) {
            if (!product) return;
            const alreadySelected = selectedProducts.some((p) => productKey(p) === productKey(product));
            const updated = alreadySelected ? selectedProducts : [...selectedProducts, product];
            onProductsChange?.(updated);
            setFieldValue("products", updated);
        } else {
            setSelectedProduct(product);
            setFieldValue("products", product ? [product] : []);
        }
    };

    const handleRemoveProduct = (productToRemove: Product) => {
        const updated = selectedProducts.filter((p) => productKey(p) !== productKey(productToRemove));
        onProductsChange?.(updated);
        setFieldValue("products", updated);
    };

    const handleProductCreated = (data?: any) => {
        if (!data) return;
        const newProduct: Product = {
            id: data.id,
            desireid: data.desireid || data.id,
            title: data.title || data.name || pendingProductName,
            image: data.image,
            brands: data.brands || []
        };
        handleSelectProduct(newProduct);
        setShowProductModal(false);
        setPendingProductName("");
    };

    const noOptionsText = useMemo(() => {
        if (requireBrand && !isFetchableBrandId(selectedBrandId))
            return "Select a brand to view associated products";
        if (hasValidProducts === false) return allowAdd ? "Start typing to add a new product" : "No products found";
        return "No options";
    }, [hasValidProducts, selectedBrandId, requireBrand, allowAdd]);

    return (
        <Box>
            {multiple && selectedProducts.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                    {selectedProducts.map((p) => (
                        <Chip
                            key={productKey(p)}
                            label={displayTitle(p)}
                            avatar={
                                p.image ? (
                                    <img
                                        src={p.image}
                                        alt=""
                                        style={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: 4,
                                            objectFit: "cover"
                                        }}
                                    />
                                ) : undefined
                            }
                            onDelete={disabled ? undefined : () => handleRemoveProduct(p)}
                            sx={{
                                borderRadius: "8px",
                                fontFamily: "CircularStd",
                                fontSize: 13,
                                fontWeight: 450
                            }}
                        />
                    ))}
                </Box>
            )}
            <Autocomplete
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                isOptionEqualToValue={(option, value) =>
                    !isAddNewOption(option) && !isAddNewOption(value) && productKey(option) === productKey(value)
                }
                getOptionLabel={(option) => (isAddNewOption(option) ? option.title : displayTitle(option)) || ""}
                // Widen to ProductOption[] so MUI infers the option type as the
                // Product | AddNewOption union (filterOptions injects AddNewOption).
                options={
                    (multiple
                        ? options.filter((opt) => !selectedProducts.some((sp) => productKey(sp) === productKey(opt)))
                        : options) as ProductOption[]
                }
                value={multiple ? null : selectedProduct || null}
                inputValue={inputText}
                disabled={disabled}
                disableClearable={multiple ? true : !selectedProduct}
                noOptionsText={noOptionsText}
                onInputChange={(_, newVal, reason) => {
                    // After a pick in multi mode, MUI fires onInputChange with the
                    // selected option's label and reason "reset". Ignore it so the
                    // search stays empty and the full list shows on next open.
                    if (multiple && reason === "reset") {
                        searchInput.current = "";
                        setInputText("");
                        return;
                    }

                    searchInput.current = newVal;
                    setInputText(newVal);

                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    timeoutRef.current = setTimeout(() => {
                        fetchProducts();
                    }, 500);
                }}
                onChange={(_, value) => {
                    if (!value) {
                        if (!multiple) {
                            handleSelectProduct(null);
                            setFieldValue("product_name", "");
                            setFieldValue("product_description", "");
                        }
                        setInputText("");
                        searchInput.current = "";
                        if (timeoutRef.current) clearTimeout(timeoutRef.current);
                        setOptions([]);
                        return;
                    }
                    if (isAddNewOption(value)) {
                        setPendingProductName(value.inputValue);
                        setShowProductModal(true);
                        setInputText("");
                        searchInput.current = "";
                    } else {
                        handleSelectProduct(value);
                        setFieldValue("product_name", value.title);
                        if (multiple) setInputText("");
                    }
                }}
                loading={loading}
                filterOptions={(opts, state) => {
                    const filtered = opts.filter(
                        (opt) =>
                            !isAddNewOption(opt) && opt.title.toLowerCase().includes(state.inputValue.toLowerCase())
                    );
                    if (
                        allowAdd &&
                        state.inputValue !== "" &&
                        filtered.length === 0 &&
                        !opts.some((opt) => !isAddNewOption(opt) && opt.title === state.inputValue)
                    ) {
                        const addOption: AddNewOption = {
                            addNew: true,
                            title: `Add "${state.inputValue}"`,
                            inputValue: state.inputValue
                        };
                        filtered.push(addOption);
                    }
                    return filtered;
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Select Product"
                        fullWidth
                        sx={selectorAutocompleteStyle}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading && <CircularProgress size={20} />}
                                    {params.InputProps.endAdornment}
                                </>
                            )
                        }}
                    />
                )}
            />

            {showProductModal && (
                <ProductV2Modal
                    onClose={() => {
                        setShowProductModal(false);
                        setPendingProductName("");
                    }}
                    onComplete={handleProductCreated}
                    initialProductName={pendingProductName}
                    selectedBrandId={selectedBrandId}
                    selectedBrandName={selectedBrandName}
                />
            )}
        </Box>
    );
};

export default InlineProductSelector;
